import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { TESTIDS } from "@/constants/testIds";
import Modal from "@/components/Modal";
import {
  Plus, Trash2, RefreshCw, Search, Users as UsersIcon, ShieldCheck,
  Crown, Save, CheckSquare, Square, KeyRound, Mail, Copy,
} from "lucide-react";

const ROLES = ["admin", "user", "warehouse", "reseller", "admin_reseller", "suspended"];

const PAGE_LABELS = {
  dashboard: "Dashboard",
  messages: "Internal Chat",
  "purchase-orders": "Purchase Orders",
  companies: "Companies",
  contacts: "Contacts",
  marketing: "Marketing",
  pipeline: "Orders Pipeline",
  inventory: "Inventory",
  dockets: "Dockets",
  portal: "Reseller Portal",
};

const EDITABLE_ROLE_COLUMNS = [
  { key: "user", label: "Staff" },
  { key: "warehouse", label: "Warehouse" },
  { key: "reseller", label: "Reseller" },
];

const ROLE_FILTERS = [
  { key: "all", label: "All Users", match: () => true },
  { key: "admin", label: "Admins", match: (u) => u.role === "admin" },
  { key: "staff", label: "Staff", match: (u) => u.role === "user" || u.role === "warehouse" },
  { key: "reseller", label: "Resellers", match: (u) => u.role === "reseller" || u.role === "admin_reseller" },
  { key: "suspended", label: "Suspended", match: (u) => u.role === "suspended" },
];

function roleBadge(role) {
  switch (role) {
    case "admin": return "es-badge es-badge-brand";
    case "warehouse": return "es-badge es-badge-info";
    case "reseller":
    case "admin_reseller": return "es-badge es-badge-neutral";
    case "suspended": return "es-badge es-badge-error";
    default: return "es-badge es-badge-neutral";
  }
}

export default function UserManagementPage() {
  const [tab, setTab] = useState("users");

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 lg:-mx-2">
      {/* Left rail */}
      <aside className="lg:w-60 lg:flex-shrink-0">
        <div className="es-card p-4 mb-3 hidden lg:block">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center">
              <UsersIcon className="w-4 h-4 text-brand-700" />
            </div>
            <div>
              <div className="font-semibold text-sm text-zinc-900">User Management</div>
              <div className="text-[11px] text-zinc-500">Users & Access</div>
            </div>
          </div>
        </div>
        {/* Mobile: pill tabs */}
        <div className="flex lg:hidden gap-2 mb-2">
          <button
            data-testid="um-tab-users"
            onClick={() => setTab("users")}
            className={[
              "flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
              tab === "users" ? "bg-brand-600 text-white" : "bg-white text-zinc-700 ring-1 ring-zinc-200",
            ].join(" ")}
          >
            Users
          </button>
          <button
            data-testid="um-tab-access"
            onClick={() => setTab("access")}
            className={[
              "flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
              tab === "access" ? "bg-brand-600 text-white" : "bg-white text-zinc-700 ring-1 ring-zinc-200",
            ].join(" ")}
          >
            Page Access
          </button>
          <button
            data-testid="um-tab-activity"
            onClick={() => setTab("activity")}
            className={[
              "flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
              tab === "activity" ? "bg-brand-600 text-white" : "bg-white text-zinc-700 ring-1 ring-zinc-200",
            ].join(" ")}
          >
            Activity
          </button>
        </div>
        {/* Desktop: side nav */}
        <nav className="hidden lg:block space-y-1">
          <SideBtn active={tab === "users"} onClick={() => setTab("users")} testid="um-tab-users-desktop">
            Users
          </SideBtn>
          <SideBtn active={tab === "access"} onClick={() => setTab("access")} testid="um-tab-access-desktop">
            Page Access by Role
          </SideBtn>
          <SideBtn active={tab === "activity"} onClick={() => setTab("activity")} testid="um-tab-activity-desktop">
            Login Activity
          </SideBtn>
        </nav>
      </aside>

      {/* Right panel */}
      <section className="flex-1 min-w-0">
        {tab === "users" && <UsersTab />}
        {tab === "access" && <AccessTab />}
        {tab === "activity" && <ActivityTab />}
      </section>
    </div>
  );
}

function SideBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={[
        "w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors",
        active
          ? "bg-brand-50 text-brand-800 font-semibold ring-1 ring-brand-200"
          : "text-zinc-700 hover:bg-zinc-100",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab 1 — Users
// ────────────────────────────────────────────────────────────────────────────
function UsersTab() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [resetting, setResetting] = useState(null);  // user object currently in the reset-password modal
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/companies")]);
      setUsers(u.data); setCompanies(c.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = Object.fromEntries(
    ROLE_FILTERS.map((f) => [f.key, users.filter(f.match).length])
  );

  const onRoleChange = async (id, role) => {
    try {
      const { data } = await api.patch(`/users/${id}`, { role });
      setUsers((us) => us.map((u) => u.id === id ? data : u));
      toast.success(`Role updated to ${role}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onCompanyChange = async (id, Company_ID) => {
    try {
      const { data } = await api.patch(`/users/${id}`, { Company_ID: Company_ID || null });
      setUsers((us) => us.map((u) => u.id === id ? data : u));
      toast.success(`Company updated.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); setUsers((us) => us.filter((u) => u.id !== id)); toast.success("User deleted."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const filterDef = ROLE_FILTERS.find((f) => f.key === filter) || ROLE_FILTERS[0];
  const visible = users
    .filter(filterDef.match)
    .filter((u) => {
      if (!q) return true;
      const needle = q.toLowerCase();
      return (u.full_name || "").toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle);
    });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Users</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage users, roles, and company access</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={load} data-testid="um-refresh" className="es-btn-secondary">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button data-testid={TESTIDS.newUserBtn} onClick={() => setShowNew(true)} className="es-btn-primary">
            <Plus className="w-4 h-4" /> New user
          </button>
        </div>
      </div>

      {/* Filter pills + search */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {ROLE_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`um-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={[
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5",
              filter === f.key
                ? "bg-brand-600 text-white border-brand-600"
                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300",
            ].join(" ")}
          >
            {f.label}
            <span className={[
              "min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center",
              filter === f.key ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-600",
            ].join(" ")}>{counts[f.key] || 0}</span>
          </button>
        ))}
        <div className="relative w-full sm:flex-1 sm:min-w-[220px] sm:max-w-md sm:ml-auto">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="um-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            className="es-input pl-9"
          />
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700 mb-4">{err}</div>}

      <div className="es-card overflow-x-auto">
        <div className="px-4 py-2 text-xs text-zinc-500 border-b border-zinc-100 flex items-center justify-between">
          <span>{visible.length} user{visible.length === 1 ? "" : "s"}</span>
        </div>
        <table className="es-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Company (Resellers)</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center py-10 text-xs text-zinc-500">Loading…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-xs text-zinc-500">No users match.</td></tr>
            )}
            {visible.map((u) => (
              <tr key={u.id} data-testid={TESTIDS.userRow(u.id)}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-50 text-brand-800 ring-1 ring-brand-200 flex items-center justify-center font-bold text-sm">
                      {(u.full_name || u.email)[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-zinc-900">{u.full_name || "—"}</div>
                      <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className={roleBadge(u.role)}>
                      {u.role === "admin" && <Crown className="w-3 h-3 inline mr-1" />}{u.role}
                    </span>
                    <select
                      data-testid={`um-role-${u.id}`}
                      value={u.role}
                      onChange={(e) => onRoleChange(u.id, e.target.value)}
                      className="es-select h-8 text-xs"
                    >
                      {ROLES.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </td>
                <td>
                  {(u.role === "reseller" || u.role === "admin_reseller") ? (
                    <select
                      data-testid={`um-company-${u.id}`}
                      value={u.Company_ID || ""}
                      onChange={(e) => onCompanyChange(u.id, e.target.value)}
                      className="es-select h-8 text-xs min-w-[200px]"
                    >
                      <option value="">— none —</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
                    </select>
                  ) : (
                    <span className="text-xs text-zinc-400">—</span>
                  )}
                </td>
                <td className="text-xs text-zinc-500">{u.created_date?.slice(0, 10) || "—"}</td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      data-testid={`um-reset-${u.id}`}
                      onClick={() => setResetting(u)}
                      className="text-zinc-400 hover:text-brand-600 p-1"
                      title="Reset password"
                      aria-label={`Reset password for ${u.email}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(u.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewUserModal
          companies={companies}
          onClose={() => setShowNew(false)}
          onCreated={(u) => { setUsers((prev) => [u, ...prev]); setShowNew(false); }}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const toast = useToast();
  // Default to the email flow — it's the safer option for the recipient.
  const [mode, setMode] = useState("email");      // "email" | "manual"
  const [newPwd, setNewPwd] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recoveryLink, setRecoveryLink] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(""); setRecoveryLink("");
    try {
      if (mode === "manual" && newPwd.length < 8) {
        setErr("Password must be at least 8 characters."); setBusy(false); return;
      }
      const payload = mode === "email"
        ? { mode: "email" }
        : { mode: "manual", new_password: newPwd, force_change: forceChange };
      const { data } = await api.post(`/users/${user.id}/admin-reset-password`, payload);
      if (mode === "email") {
        if (data.delivered) {
          toast.success(`Reset link emailed to ${data.email}. Link is valid for 1 hour.`);
          onDone();
        } else {
          // SMTP isn't configured — surface the link so the admin can copy it.
          setRecoveryLink(data.recovery_link || "");
          toast.warning("SMTP is not configured — copy the link below and share it with the user.");
        }
      } else {
        toast.success(
          forceChange
            ? `Password set for ${data.email}. They'll be prompted to change it on next login.`
            : `Password set for ${data.email}.`
        );
        onDone();
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(recoveryLink); toast.success("Link copied to clipboard."); }
    catch { toast.error("Could not copy. Select the link manually."); }
  };

  return (
    <Modal
      title={`Reset password — ${user.full_name || user.email}`}
      icon={<KeyRound className="w-5 h-5 text-brand-600" />}
      onClose={onClose}
      size="lg"
    >
      <form onSubmit={submit} className="px-6 py-5 space-y-4">
        <div className="text-sm text-zinc-600">
          You&apos;re resetting the password for <span className="font-mono font-semibold text-zinc-900">{user.email}</span>.
        </div>

        {/* Mode picker */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="reset-mode-email"
            onClick={() => setMode("email")}
            className={[
              "px-4 py-3 rounded-lg border text-left transition-colors",
              mode === "email" ? "border-brand-500 bg-brand-50/60 ring-2 ring-brand-200" : "border-zinc-200 hover:border-zinc-300",
            ].join(" ")}
          >
            <div className="flex items-center gap-2 font-semibold text-sm text-zinc-900">
              <Mail className="w-4 h-4 text-brand-600" /> Email a reset link
            </div>
            <div className="text-xs text-zinc-500 mt-1">User picks their own new password. Valid for 1 hour.</div>
          </button>
          <button
            type="button"
            data-testid="reset-mode-manual"
            onClick={() => setMode("manual")}
            className={[
              "px-4 py-3 rounded-lg border text-left transition-colors",
              mode === "manual" ? "border-brand-500 bg-brand-50/60 ring-2 ring-brand-200" : "border-zinc-200 hover:border-zinc-300",
            ].join(" ")}
          >
            <div className="flex items-center gap-2 font-semibold text-sm text-zinc-900">
              <KeyRound className="w-4 h-4 text-brand-600" /> Set password manually
            </div>
            <div className="text-xs text-zinc-500 mt-1">You type the new password. Useful for offline users.</div>
          </button>
        </div>

        {mode === "manual" && (
          <div className="space-y-3 border border-zinc-200 rounded-lg p-3 bg-zinc-50/40">
            <div>
              <label className="es-label" htmlFor="reset-new-pwd">New password (min 8 chars)</label>
              <input
                id="reset-new-pwd"
                data-testid="reset-new-pwd"
                type="text"
                minLength={8}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                className="es-input font-mono"
                placeholder="e.g. Sunshine!Quarry42"
                autoComplete="new-password"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Plain-text display so you can copy it once. Don&apos;t reuse a real password — this gets typed where someone might see your screen.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="reset-force-change"
                checked={forceChange}
                onChange={(e) => setForceChange(e.target.checked)}
                className="w-4 h-4"
              />
              Force user to choose a new password on next login (recommended)
            </label>
          </div>
        )}

        {recoveryLink && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
            <div className="text-xs font-semibold text-amber-900">SMTP unavailable — copy this link to share with the user:</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[10px] font-mono bg-white border border-amber-300 rounded px-2 py-1.5 truncate" title={recoveryLink}>{recoveryLink}</code>
              <button type="button" onClick={copyLink} className="es-btn-secondary px-2 py-1.5"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className="es-btn-secondary" disabled={busy}>Cancel</button>
          <button type="submit" data-testid="reset-submit" disabled={busy || (mode === "manual" && newPwd.length < 8)} className="es-btn-primary">
            {busy ? "Working…" : (mode === "email" ? "Send reset link" : "Set password")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab 2 — Page Access by Role
// ────────────────────────────────────────────────────────────────────────────
function AccessTab() {
  const toast = useToast();
  const [pages, setPages] = useState([]);
  const [perms, setPerms] = useState(null); // {user:[], warehouse:[], reseller:[], admin_reseller:[]}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/role-permissions");
        setPages(data.pages || []);
        setPerms(data.permissions || {});
      } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, []);

  const toggle = (role, page) => {
    setPerms((p) => {
      const set = new Set(p[role] || []);
      set.has(page) ? set.delete(page) : set.add(page);
      // mirror reseller ↔ admin_reseller (they share visibility)
      const next = { ...p, [role]: Array.from(set) };
      if (role === "reseller") next.admin_reseller = Array.from(set);
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.patch("/role-permissions", perms);
      setPerms(data.permissions);
      toast.success("Permissions saved.");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg); toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!perms) return <div className="text-xs text-zinc-500">Loading permissions…</div>;

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Page Access by Role</h1>
        <p className="text-sm text-zinc-500 mt-1">Control which sidebar pages each role can access</p>
      </div>

      <div className="es-card overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand-600" />
          <h3 className="font-semibold text-zinc-900">Sidebar Menu Access by Role</h3>
          <span className="text-xs text-zinc-400">(Edit with caution)</span>
        </div>

        <div className="overflow-x-auto">
          <table className="es-table">
            <thead>
              <tr>
                <th>Page</th>
                <th className="text-center">Admin</th>
                {EDITABLE_ROLE_COLUMNS.map((c) => (
                  <th key={c.key} className="text-center">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page} data-testid={`acl-row-${page}`}>
                  <td className="font-medium text-zinc-800">{PAGE_LABELS[page] || page}</td>
                  <td className="text-center">
                    <CheckSquare className="w-4 h-4 text-zinc-300 inline" />
                  </td>
                  {EDITABLE_ROLE_COLUMNS.map((c) => {
                    const checked = (perms[c.key] || []).includes(page);
                    // Portal: only resellers can have it.
                    const lockOff = page === "portal" && c.key !== "reseller";
                    return (
                      <td key={c.key} className="text-center">
                        <button
                          data-testid={`acl-${c.key}-${page}`}
                          disabled={lockOff}
                          onClick={() => !lockOff && toggle(c.key, page)}
                          className={[
                            "inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors",
                            lockOff ? "opacity-40 cursor-not-allowed" : "hover:bg-zinc-100",
                          ].join(" ")}
                          title={lockOff ? "Not available for this role" : ""}
                        >
                          {checked
                            ? <CheckSquare className="w-5 h-5 text-brand-600" />
                            : <Square className="w-5 h-5 text-zinc-300" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-3">
          {err && <span className="text-xs text-red-700 mr-auto">{err}</span>}
          <button data-testid="acl-save" disabled={saving} onClick={save} className="es-btn-primary">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Permissions"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// New user modal
// ────────────────────────────────────────────────────────────────────────────
function NewUserModal({ companies, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "user", Company_ID: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...form, Company_ID: form.Company_ID || null };
      const { data } = await api.post("/users", payload);
      onCreated(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal title="New User" onClose={onClose}>
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <div>
          <label className="es-label">Full name *</label>
          <input data-testid="new-user-full_name" required value={form.full_name} onChange={(e) => u("full_name", e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Email *</label>
          <input data-testid="new-user-email" type="email" required value={form.email} onChange={(e) => u("email", e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Password *</label>
          <input data-testid="new-user-password" type="password" required minLength={6} value={form.password} onChange={(e) => u("password", e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Role</label>
          <select data-testid="new-user-role" value={form.role} onChange={(e) => u("role", e.target.value)} className="es-select w-full">
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="es-label">Company (resellers)</label>
          <select data-testid="new-user-company" value={form.Company_ID} onChange={(e) => u("Company_ID", e.target.value)} className="es-select w-full">
            <option value="">— none —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
          </select>
        </div>

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} data-testid="new-user-save" className="es-btn-primary">
            {saving ? "Saving…" : "Create user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab 3 — Login Activity
// ────────────────────────────────────────────────────────────────────────────
function ActivityTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");   // all | success | failed
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/auth/activity", { params: { limit: 200 } });
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = items.filter((r) => {
    if (filter === "success" && r.status !== "success") return false;
    if (filter === "failed" && r.status === "success") return false;
    if (q.trim()) {
      const t = q.toLowerCase();
      if (!(r.email || "").toLowerCase().includes(t) &&
          !(r.ip || "").toLowerCase().includes(t)) return false;
    }
    return true;
  });

  const successCount = items.filter((r) => r.status === "success").length;
  const failedCount = items.length - successCount;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Login Activity</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every login attempt is logged with the account, outcome, IP address, and browser. Newest first, last 200 events.
          </p>
        </div>
        <button onClick={load} className="es-btn-secondary" data-testid="activity-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Summary chips + filter row */}
      <div className="flex items-center flex-wrap gap-3">
        <FilterChip label={`All ${items.length}`} active={filter === "all"} onClick={() => setFilter("all")} testid="activity-filter-all" />
        <FilterChip label={`Successful ${successCount}`} active={filter === "success"} onClick={() => setFilter("success")} colour="emerald" testid="activity-filter-success" />
        <FilterChip label={`Failed ${failedCount}`} active={filter === "failed"} onClick={() => setFilter("failed")} colour="red" testid="activity-filter-failed" />
        <div className="relative flex-1 max-w-sm ml-auto">
          <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="activity-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or IP…"
            className="es-input pl-8 h-9 text-sm"
            autoComplete="off"
          />
        </div>
      </div>

      {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

      <div className="es-card overflow-hidden">
        <table className="es-table" data-testid="activity-table">
          <thead>
            <tr>
              <th className="w-44">When</th>
              <th>Account</th>
              <th className="w-32">Outcome</th>
              <th className="w-40">IP</th>
              <th>Browser</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center text-zinc-400 py-8">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-zinc-400 py-8">No login events match.</td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.id}>
                <td className="text-xs font-mono text-zinc-600 whitespace-nowrap">{formatWhen(r.at)}</td>
                <td className="text-sm">
                  <div className="font-mono text-zinc-900">{r.email || "—"}</div>
                  {!r.user_id && r.status === "unknown_user" && (
                    <div className="text-[10px] text-zinc-400 italic">(no matching account)</div>
                  )}
                </td>
                <td><OutcomeBadge status={r.status} /></td>
                <td className="text-xs font-mono text-zinc-600">{r.ip || "—"}</td>
                <td className="text-xs text-zinc-500 truncate max-w-md" title={r.user_agent || ""}>{shortUA(r.user_agent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick, colour = "brand", testid }) {
  const activeClass = {
    brand: "bg-brand-600 text-white",
    emerald: "bg-emerald-600 text-white",
    red: "bg-red-600 text-white",
  }[colour];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${active ? activeClass : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
    >
      {label}
    </button>
  );
}

function OutcomeBadge({ status }) {
  const map = {
    success:          { label: "Success",       cls: "bg-emerald-100 text-emerald-800" },
    invalid_password: { label: "Wrong password", cls: "bg-red-100 text-red-800" },
    unknown_user:     { label: "Unknown user",  cls: "bg-amber-100 text-amber-800" },
    suspended:        { label: "Suspended",     cls: "bg-zinc-100 text-zinc-700" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-zinc-100 text-zinc-700" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const dateStr = sameDay ? "" : d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }) + " ";
    const timeStr = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return dateStr + timeStr;
  } catch { return iso; }
}

// Shortens a raw User-Agent to a friendly "Chrome on macOS" style string.
// Not exhaustive — just picks up the platform + browser buckets we care about.
function shortUA(ua) {
  if (!ua) return "—";
  const os =
    /iPhone|iPad|iOS/i.test(ua) ? "iOS" :
    /Android/i.test(ua)         ? "Android" :
    /Mac OS X|macOS/i.test(ua)  ? "macOS" :
    /Windows/i.test(ua)         ? "Windows" :
    /Linux/i.test(ua)           ? "Linux" : "—";
  const br =
    /Edg\//i.test(ua)     ? "Edge" :
    /Chrome/i.test(ua)    ? "Chrome" :
    /Firefox/i.test(ua)   ? "Firefox" :
    /Safari/i.test(ua)    ? "Safari" : "Browser";
  return `${br} on ${os}`;
}

